import getCookies from './getCookies';
import isClient from './isClient';

const ALGOLIA_USER_TOKEN_COOKIE = 'jb_algolia_user_token';

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,129}$/;
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

// Browser-only. A module-level cache on the server would leak across requests.
let browserToken = '';

const normalizeToken = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim().replace(/^"|"$/g, '');
  return TOKEN_PATTERN.test(trimmed) ? trimmed : '';
};

const createToken = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `u${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

const readStoreCookie = (cookieStore, name) => {
  if (!cookieStore || typeof cookieStore.get !== 'function') return '';
  const entry = cookieStore.get(name);
  if (typeof entry === 'string') return entry;
  return typeof entry?.value === 'string' ? entry.value : '';
};

// Missing OneTrust state is unknown, not a decline.
export const shouldSkipAlgoliaUserToken = ({
  globalPrivacyControl = false,
  onetrustActiveGroups = '',
} = {}) => {
  if (globalPrivacyControl) return true;
  if (!onetrustActiveGroups) return false;
  return onetrustActiveGroups.split(',').length <= 4;
};

const hasDeclinedAlgoliaConsent = () => {
  if (!isClient()) return false;

  return shouldSkipAlgoliaUserToken({
    globalPrivacyControl: navigator.globalPrivacyControl === true,
    onetrustActiveGroups:
      typeof window.OnetrustActiveGroups === 'string'
        ? window.OnetrustActiveGroups
        : '',
  });
};

// SDK v3 encrypts the rl_anonymous_id cookie and localStorage value.
// getAnonymousId() is the only decrypted id, and the pre-load stub lacks it.
const readRudderAnonymousId = () => {
  if (!isClient()) return '';

  try {
    return normalizeToken(window.rudderanalytics?.getAnonymousId?.());
  } catch {
    return '';
  }
};

const persistBrowserAlgoliaUserToken = token => {
  if (!isClient() || hasDeclinedAlgoliaConsent()) return;

  const normalized = normalizeToken(token);
  if (!normalized) return;

  browserToken = normalized;
  if (getCookies().get(ALGOLIA_USER_TOKEN_COOKIE) === normalized) return;

  getCookies().set(ALGOLIA_USER_TOKEN_COOKIE, normalized, {
    path: '/',
    maxAge: ONE_YEAR_SECONDS,
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
  });
};

export const getOrCreateBrowserAlgoliaUserToken = () => {
  if (!isClient() || hasDeclinedAlgoliaConsent()) return '';

  const cached = normalizeToken(browserToken);
  if (cached) return cached;

  const fromCookie = normalizeToken(
    getCookies().get(ALGOLIA_USER_TOKEN_COOKIE)
  );
  if (fromCookie) {
    browserToken = fromCookie;
    return fromCookie;
  }

  const token = readRudderAnonymousId() || createToken();
  persistBrowserAlgoliaUserToken(token);
  return token;
};

export const rememberAlgoliaUserToken = token => {
  if (hasDeclinedAlgoliaConsent()) return '';

  const normalized = normalizeToken(token);
  if (!normalized) return getOrCreateBrowserAlgoliaUserToken();
  persistBrowserAlgoliaUserToken(normalized);
  return normalized;
};

// Existing jb cookie only. No mint here: bots and first visits must not each
// become a new Algolia user. The browser creates the token after mount.
export const resolveAlgoliaUserTokenFromStore = cookieStore =>
  normalizeToken(readStoreCookie(cookieStore, ALGOLIA_USER_TOKEN_COOKIE));
